import { deriveShadowSuffix } from "./streetMatching";

// The address text fields autocomplete can standardize. Country is excluded:
// it isn't a standardized text value, and the ISO/ID split would false-positive.
// The street key is the hook's `addr1` by default; surfaces that store the
// street under a different key (the numeric-`countryID` surfaces use `street`)
// pass `streetField` so the diff/undo run against the right key.
export const STANDARDIZED_FIELDS = ["addr1", "city", "state", "zip", "plus4"];
const standardizedFields = (streetField) => [
  streetField,
  "city",
  "state",
  "zip",
  "plus4",
];
// Fields that render a shadow ghost suffix (plus4 is computed, never ghosted).
const shadowedFields = (streetField) => [streetField, "city", "state", "zip"];

const normalizeField = (value) => (value || "").trim().toLowerCase();

// What the user actually saw for a field: their typed text plus the ghost
// suffix that would complete it to the standardized value. The ghost is exactly
// `deriveShadowSuffix(typed, standardized)` — the same suffix the autocomplete
// previewed live — so we reconstruct it from the saved value rather than depend
// on the live `shadows` state, which has already collapsed by the time a Save
// commits (focus left the field, verification cleared the candidate).
const seenValue = (typed, standardized, field, streetField) => {
  const typedField = typed?.[field] || "";
  if (!shadowedFields(streetField).includes(field)) return typedField;
  return (
    typedField + deriveShadowSuffix(typedField, standardized?.[field] || "")
  );
};

/**
 * Did the saved address differ from what the user *saw* — not merely from what
 * they typed? The ghost preview is part of what they saw, so accepting a
 * completion they were already shown (e.g. "123 m" -> "123 Main St") is no
 * surprise. The genuine surprise is a change the ghost never showed — a USPS
 * abbreviation applied to a fully-typed token ("west" -> "W", "highway" ->
 * "Hwy"), where the typed word has no visible ghost suffix. Compared
 * case-insensitively so pure casing normalization isn't flagged.
 *
 * Shared by both address surfaces (the inline pencil editor and the cell
 * popover) so "did this surprise the user" has exactly one definition.
 *
 * @param {object} typed - the address as the user typed it (pre-standardization)
 * @param {object} standardized - the address that was saved (post-autofill)
 * @param {string} [streetField] - the key the street lives under (default "addr1")
 * @returns {boolean}
 */
export const savedDiffersFromSeen = (typed, standardized, streetField = "addr1") =>
  standardizedFields(streetField).some(
    (field) =>
      normalizeField(seenValue(typed, standardized, field, streetField)) !==
      normalizeField(standardized?.[field])
  );

/**
 * Build the address to re-persist when the user undoes a standardization.
 *
 * Undo restores what the user *saw*, not the raw text they typed — so it must
 * key off `seenValue` (their typed text + the accepted forward-completion ghost),
 * the SAME definition the surprise-detection (`savedDiffersFromSeen`) uses.
 * Overlaying the raw `typed` would drop a completion the user already accepted
 * ("8602 w. grand" + accepted ghost " Ledge Hwy" → undo "8602 w. grand", losing
 * "Ledge Hwy"). The only thing undo reverts is the canonical re-spelling of
 * tokens the user already typed — which is exactly what `seenValue` excludes.
 *
 * Undo must also NOT blank fields the user never typed. When a cell starts empty
 * and the user types only the street, autocomplete fills city/state/zip from the
 * candidate; the user saw those ghost in, so they stay. So start from the
 * standardized address and overlay only the per-field `seenValue` the user
 * actually saw. For a fully-typed token with no ghost suffix, `seenValue` equals
 * the typed text, so the rule degenerates to "restore what they typed."
 *
 * Non-standardized fields on `standardized` (e.g. countryID) pass through
 * untouched.
 *
 * @param {object} typed - the address as the user typed it (pre-standardization)
 * @param {object} standardized - the address that was saved (post-autofill)
 * @param {string} [streetField] - the key the street lives under (default "addr1")
 * @returns {object} the merged undo payload
 */
export const buildUndoPayload = (typed, standardized, streetField = "addr1") => {
  const merged = { ...standardized };
  standardizedFields(streetField).forEach((field) => {
    // A whitespace-only typed field is not something the user typed, so there's
    // no "saw" to restore — leave the standardized (filled) value standing.
    if (!(typed?.[field] || "").trim()) return;
    merged[field] = seenValue(typed, standardized, field, streetField);
  });
  return merged;
};

export const buildRevertedAddress = (standardization) => ({
  ...standardization,
  isVerified: false,
  verificationProvider: "",
  verifiedAt: "",
  validationSkipped: "1",
});
